import { AlertCircle, CheckCircle2, X } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { ToastContext, type ToastApi } from '@/lib/toast-context';

import { cn } from '@/lib/utils';

/*
 * Toasts.
 *
 * Hand-rolled rather than another dependency, because the whole thing is a list
 * and an `aria-live` region — and the `aria-live` is the part that matters.
 * design-system.md §9.5 asks for it by name: a save confirmation that only
 * appears visually is a save confirmation half the users never receive.
 *
 * `polite` for a success, `assertive` for a failure. A saved record can wait for
 * a gap in the screen reader's speech; a failed one cannot, because the person
 * is about to move on believing it worked.
 */

export type ToastTone = 'success' | 'error';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

/** How long a toast stays. Errors stay until dismissed. */
const SUCCESS_DURATION_MS = 5_000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { id, tone, message }]);

      /*
       * A failure stays until it is dismissed. Whatever it is telling you did
       * not happen, and a message about unsaved work that removes itself after
       * five seconds is a message that gets missed.
       */
      if (tone === 'success') {
        setTimeout(() => {
          dismiss(id);
        }, SUCCESS_DURATION_MS);
      }
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => {
        push('success', message);
      },
      error: (message) => {
        push('error', message);
      },
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/*
        Two regions, not one. `aria-live` politeness is a property of the
        region, so a single container would force successes and failures to
        share an urgency — and the wrong one either interrupts constantly or
        announces a failure too late to matter.
      */}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-[calc(100vw-2rem)] max-w-sm flex-col gap-2">
        <ToastRegion
          politeness="polite"
          toasts={toasts.filter((toast) => toast.tone === 'success')}
          onDismiss={dismiss}
        />
        <ToastRegion
          politeness="assertive"
          toasts={toasts.filter((toast) => toast.tone === 'error')}
          onDismiss={dismiss}
        />
      </div>
    </ToastContext.Provider>
  );
}

function ToastRegion({
  politeness,
  toasts,
  onDismiss,
}: {
  politeness: 'polite' | 'assertive';
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div aria-live={politeness} className="flex flex-col gap-2">
      {toasts.map((toast) => {
        const Icon = toast.tone === 'success' ? CheckCircle2 : AlertCircle;

        return (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-body shadow-pop',
              toast.tone === 'success'
                ? 'border-status-completed/30 bg-white text-status-completed-text'
                : 'border-status-priority-text/30 bg-white text-status-priority-text',
            )}
          >
            <Icon aria-hidden="true" className="mt-0.5 size-[18px] shrink-0" />
            <p className="min-w-0 flex-1">{toast.message}</p>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => {
                onDismiss(toast.id);
              }}
              // -m-2 p-2 keeps the 44px hit area without a 44px-tall toast.
              className="-m-2 shrink-0 p-2 text-ink-500 hover:text-ink-900"
            >
              <X aria-hidden="true" className="size-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
